import { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { MockBanner } from "@/components/layout/MockBanner";
import { EmailVerificationBanner } from "@/features/auth/components/EmailVerificationBanner";
import { PastDueBanner } from "@/features/subscription/components/PastDueBanner";
import { useSidebarMode } from "@/components/layout/useSidebarMode";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useNotificationsConnection } from "@/features/notifications/hooks/useNotificationsConnection";
import { DisconnectedBanner } from "@/features/notifications/components/DisconnectedBanner";
import { PlanActivationModal } from "@/features/subscription/components/PlanActivationModal";
import { cn } from "@/lib/utils/cn";

export function AppLayout() {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const refresh = useAuthStore((s) => s.refresh);
  const location = useLocation();
  const { collapsed, hidden, toggleCollapse, toggleHidden } = useSidebarMode();

  // Proactive refresh on cold load: a persisted session has no in-memory access
  // token, so without this the first protected request would 401 before the
  // interceptor refreshes (#82). Block protected content until the token is back.
  const [booting, setBooting] = useState(isAuthenticated && !accessToken);
  useEffect(() => {
    if (isAuthenticated && !accessToken) {
      setBooting(true);
      void refresh().finally(() => setBooting(false));
    } else {
      setBooting(false);
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useNotificationsConnection();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (booting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-[13px] text-ink-muted">
        {t("states.loadingSession")}
      </div>
    );
  }

  // Gate de onboarding (backstop p/ acesso direto por URL): só entra no painel quem
  // completou o perfil E aceitou os termos vigentes. `=== false` evita trancar sessões
  // legadas cujos flags ainda não vieram do backend (undefined) — o refresh os preenche.
  if (user?.profileCompleted === false) {
    return <Navigate to="/onboarding/complete-profile" replace />;
  }
  if (user?.termsAccepted === false) {
    return <Navigate to="/onboarding/terms" replace />;
  }

  return (
    // zoom 0.85 reduz a densidade ~15%; minHeight compensa o zoom pra o layout
    // (e a sidebar dark) preencher a viewport inteira, sem faixa clara no rodapé.
    <div className="flex bg-bg" style={{ zoom: 0.85, minHeight: "calc(100vh / 0.85)" }}>
      {/* Desktop sidebar — wrapper anima width pra 0 quando hidden */}
      <div
        className={cn(
          "sticky top-0 hidden shrink-0 transition-[width] duration-300 ease-out lg:block",
          hidden ? "w-0" : collapsed ? "w-[60px]" : "w-[228px]",
        )}
        style={{ height: "calc(100vh / 0.85)" }}
      >
        <div
          className={cn(
            "absolute inset-y-0 left-0 transition-transform duration-300 ease-out",
            hidden && "-translate-x-full",
          )}
        >
          <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-0 z-40 lg:hidden",
          drawerOpen ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <div
          className={cn(
            "absolute inset-0 bg-black/50 transition-opacity",
            drawerOpen ? "opacity-100" : "opacity-0",
          )}
          onClick={() => setDrawerOpen(false)}
        />
        <div
          className={cn(
            "absolute inset-y-0 left-0 h-full transition-transform",
            drawerOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <Sidebar onItemClick={() => setDrawerOpen(false)} />
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <MockBanner />
        {/* Antes do de e-mail: perder o acesso por falta de pagamento é mais urgente que confirmar
            o e-mail, e quem tem os dois pendentes precisa ver primeiro o que tem prazo. */}
        <PastDueBanner />
        <EmailVerificationBanner />
        <DisconnectedBanner />
        <Topbar
          onMenuClick={() => setDrawerOpen(true)}
          sidebarHidden={hidden}
          onToggleSidebar={toggleHidden}
        />
        <main className="flex-1 overflow-y-auto p-2.5 md:p-4 lg:p-5">
          <Outlet />
        </main>
      </div>
      <PlanActivationModal />
    </div>
  );
}
